import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReservationRow } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { can } from '../lib/perms';

/**
 * GoPay / BitPay / Fakturace CK — the legacy edit form's three admin-only
 * amount fields, on the detail's payment card. Shown only to a user with
 * the /payments/* grant (the admin); absolute amounts, saved together.
 */
export function PrepaidForm({ r }: { r: ReservationRow }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const [open, setOpen] = useState(false);
  const [gopay, setGopay] = useState(String(r.paidGopay));
  const [bitpay] = useState(String(r.paidBitpay));
  const [invoice, setInvoice] = useState(String(r.paidInvoice));
  const m = useMutation({
    mutationFn: () => api.setPrepaid(r.id, { gopay: Number(gopay) || 0, bitpay: Number(bitpay) || 0, invoice: Number(invoice) || 0 }),
    onSuccess: async () => {
      setOpen(false);
      await qc.invalidateQueries();
    },
  });
  if (!can(me.data, '/payments/*', 'write') || r.status === 'deleted') return null;
  if (!open) {
    return (
      <button type="button" className="btn sm" onClick={() => setOpen(true)}>
        Upravit předplacené
      </button>
    );
  }
  const err = m.error;
  const message = err instanceof ApiError ? err.serverMessage ?? err.message : err instanceof Error ? err.message : null;
  const num = (label: string, v: string, set: (s: string) => void) => (
    <label className="ef">
      {label}
      <input type="number" min={0} max={65535} step={1} value={v} onChange={(e) => set(e.target.value)} />
    </label>
  );
  return (
    <form
      className="prepaidform"
      onSubmit={(e) => {
        e.preventDefault();
        if (!m.isPending) m.mutate();
      }}
    >
      {/* BitPay is not offered any more (30 September); the stored value is
          carried through unchanged so saving GoPay or CK cannot zero it. */}
      <div className="efgrid two">
        {num('GoPay (Kč)', gopay, setGopay)}
        {num('Fakturace CK (Kč)', invoice, setInvoice)}
      </div>
      <div className="efactions">
        <button type="submit" className="btn sm primary" disabled={m.isPending}>
          {m.isPending ? 'Ukládám…' : 'Uložit'}
        </button>
        <button type="button" className="btn sm" onClick={() => setOpen(false)} disabled={m.isPending}>
          Zrušit
        </button>
        {message && <span className="arrive-error" role="alert">{message}</span>}
      </div>
    </form>
  );
}
